import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ShieldCheck,
  ShieldAlert,
  Check,
  X,
  Loader2,
  FileText,
  AlertTriangle,
  Mail,
  Edit3,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { DashboardLayout } from '../components/DashboardLayout';
import { useEntityStore } from '../store';
import { reviewAPI } from '../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';

const formatCurrency = (value, currency = 'GBP') => {
  const symbols = { GBP: '\u00a3', EUR: '\u20ac', USD: '$', DKK: 'DKK ', CHF: 'CHF ', SEK: 'SEK ', NOK: 'NOK ' };
  return `${symbols[currency] || currency + ' '}${Math.abs(value).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;
};

function ReviewCard({ invoice, onApprove, onReject }) {
  const isQuarantined = invoice.review_status === 'quarantined';
  const [showCorrections, setShowCorrections] = useState(isQuarantined);
  const [corrections, setCorrections] = useState({});
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  const needsAmountCorrection = isQuarantined && !corrections.amount;

  const handleApprove = async () => {
    if (needsAmountCorrection) {
      toast.error('Quarantined invoices require an amount correction before approval');
      return;
    }
    setIsApproving(true);
    try {
      const correctionPayload = Object.keys(corrections).length > 0 ? corrections : null;
      await onApprove(invoice.id, correctionPayload);
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    setIsRejecting(true);
    try {
      await onReject(invoice.id, rejectionReason);
    } finally {
      setIsRejecting(false);
    }
  };

  const updateCorrection = (field, value) => {
    setCorrections(prev => {
      const next = { ...prev };
      if (value === '' || value === undefined) {
        delete next[field];
      } else {
        next[field] = field === 'amount' || field === 'vat_amount' || field === 'vat_rate'
          ? parseFloat(value) || 0
          : value;
      }
      return next;
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`border rounded-xl p-6 hover:shadow-lg transition-all bg-card ${isQuarantined ? 'border-red-300 dark:border-red-800' : ''}`}
    >
      {/* Quarantine banner */}
      {isQuarantined && (
        <div className="flex items-center gap-2 mb-4 p-3 bg-red-50 dark:bg-red-950/30 rounded-lg text-sm text-red-800 dark:text-red-300">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>Quarantined — amount could not be parsed from source. Provide the correct amount below to approve.</span>
        </div>
      )}

      {/* Header: vendor + amount + actions */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-semibold truncate">
              {invoice.supplier_client_name || 'Unknown Vendor'}
            </h3>
            <Badge variant="outline" className="text-xs shrink-0">
              {invoice.type === 'outbound' ? 'Revenue' : 'Expense'}
            </Badge>
            {isQuarantined && (
              <Badge variant="destructive" className="text-xs shrink-0">
                Quarantined
              </Badge>
            )}
          </div>
          <p className="text-2xl font-bold text-foreground">
            {isQuarantined ? (
              <span className="text-red-500">Amount missing</span>
            ) : (
              formatCurrency(invoice.amount, invoice.currency)
            )}
          </p>
          {invoice.vat_amount > 0 && (
            <p className="text-sm text-muted-foreground">
              VAT: {formatCurrency(invoice.vat_amount, invoice.currency)}
              {invoice.vat_rate > 0 && ` (${invoice.vat_rate}%)`}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (showRejectInput) {
                handleReject();
              } else {
                setShowRejectInput(true);
              }
            }}
            disabled={isRejecting || isApproving}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
          >
            {isRejecting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <X className="h-4 w-4 mr-1" />}
            Reject
          </Button>
          <Button
            size="sm"
            onClick={handleApprove}
            disabled={isApproving || isRejecting || needsAmountCorrection}
            className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
          >
            {isApproving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
            {needsAmountCorrection ? 'Enter Amount to Approve' : Object.keys(corrections).length > 0 ? 'Approve with Corrections' : 'Approve'}
          </Button>
        </div>
      </div>

      {/* Routing reasons (why this needs review) */}
      {invoice.review_routing_reasons && invoice.review_routing_reasons.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {invoice.review_routing_reasons.map((reason, i) => (
            <Badge key={i} variant="secondary" className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 text-xs">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {reason}
            </Badge>
          ))}
        </div>
      )}

      {/* Invoice details grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mb-4">
        <div>
          <span className="text-muted-foreground">Entity</span>
          <p className="font-medium">{invoice.entity_name || invoice.entity_id}</p>
        </div>
        <div>
          <span className="text-muted-foreground">Invoice #</span>
          <p className="font-medium">{invoice.invoice_number || '-'}</p>
        </div>
        <div>
          <span className="text-muted-foreground">Date</span>
          <p className="font-medium">{invoice.date || '-'}</p>
        </div>
        <div>
          <span className="text-muted-foreground">Category</span>
          <p className="font-medium">{invoice.category || '-'}</p>
        </div>
      </div>

      {/* Source info */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
        {invoice.email_subject && (
          <div className="flex items-center gap-1 truncate">
            <Mail className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{invoice.email_subject}</span>
          </div>
        )}
        {invoice.email_from && (
          <span className="truncate">from {invoice.email_from}</span>
        )}
        {invoice.file_url && (
          <a
            href={invoice.file_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-primary hover:underline shrink-0"
          >
            <FileText className="h-3.5 w-3.5" />
            View Document
          </a>
        )}
      </div>

      {/* Reject reason input */}
      {showRejectInput && (
        <div className="mt-3 p-3 bg-red-50 dark:bg-red-950/30 rounded-lg">
          <label className="text-sm font-medium text-red-800 dark:text-red-300 block mb-1">
            Rejection reason (optional)
          </label>
          <input
            type="text"
            value={rejectionReason}
            onChange={e => setRejectionReason(e.target.value)}
            placeholder="Why is this invoice being rejected?"
            className="w-full px-3 py-2 text-sm border rounded-md bg-background"
            onKeyDown={e => e.key === 'Enter' && handleReject()}
          />
        </div>
      )}

      {/* Corrections toggle */}
      <div className="mt-3">
        <button
          onClick={() => setShowCorrections(!showCorrections)}
          className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
        >
          <Edit3 className="h-3.5 w-3.5" />
          {showCorrections ? 'Hide corrections' : 'Approve with corrections...'}
        </button>
        {showCorrections && (
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { field: 'amount', label: 'Amount', type: 'number', current: invoice.amount, required: isQuarantined },
              { field: 'vat_amount', label: 'VAT Amount', type: 'number', current: invoice.vat_amount },
              { field: 'vat_rate', label: 'VAT Rate %', type: 'number', current: invoice.vat_rate },
              { field: 'currency', label: 'Currency', type: 'text', current: invoice.currency },
              { field: 'type', label: 'Type', type: 'text', current: invoice.type },
              { field: 'supplier_client_name', label: 'Vendor', type: 'text', current: invoice.supplier_client_name },
            ].map(({ field, label, type, current, required }) => (
              <div key={field}>
                <label className={`text-xs block mb-1 ${required && !corrections[field] ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>
                  {label}{required ? ' *' : ''}
                </label>
                <input
                  type={type}
                  step={type === 'number' ? '0.01' : undefined}
                  placeholder={required ? 'Required' : String(current ?? '')}
                  value={corrections[field] ?? ''}
                  onChange={e => updateCorrection(field, e.target.value)}
                  className={`w-full px-2 py-1.5 text-sm border rounded-md bg-background ${required && !corrections[field] ? 'border-red-400 dark:border-red-700' : ''}`}
                  autoFocus={required && !corrections[field]}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

// Standalone content (no DashboardLayout) for embedding as a tab inside InvoicesPage.
export function ReviewQueueContent() {
  const { selectedEntity } = useEntityStore();
  const [invoices, setInvoices] = useState([]);
  const [totalPending, setTotalPending] = useState(0);
  const [totalQuarantined, setTotalQuarantined] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const totalActionable = totalPending + totalQuarantined;

  const fetchQueue = async () => {
    try {
      const entityId = selectedEntity !== 'all' ? selectedEntity : undefined;
      const { data } = await reviewAPI.getQueue({ entity_id: entityId });
      setInvoices(data.invoices || []);
      setTotalPending(data.total_pending || 0);
      setTotalQuarantined(data.total_quarantined || 0);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetchQueue();
  }, [selectedEntity]);

  const handleApprove = async (invoiceId, corrections) => {
    try {
      await reviewAPI.approve(invoiceId, corrections);
      toast.success(corrections ? 'Invoice approved with corrections' : 'Invoice approved');
      await fetchQueue();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleReject = async (invoiceId, reason) => {
    try {
      await reviewAPI.reject(invoiceId, reason);
      toast.success('Invoice rejected');
      await fetchQueue();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <div className="flex flex-col gap-8" data-testid="review-queue-content">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight">Review Queue</h2>
          <p className="text-muted-foreground mt-1">
            Review AI-extracted invoices before they enter the financial ledger
          </p>
        </div>
        {totalActionable > 0 && (
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 text-base px-3 py-1">
              <ShieldAlert className="h-4 w-4 mr-1.5" />
              {totalPending} pending
            </Badge>
            {totalQuarantined > 0 && (
              <Badge variant="destructive" className="text-base px-3 py-1">
                {totalQuarantined} quarantined
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Pending Review</p>
            <p className="text-2xl font-bold">{totalPending}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Quarantined</p>
            <p className="text-2xl font-bold text-red-600">{totalQuarantined}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Showing</p>
            <p className="text-2xl font-bold">{invoices.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Status</p>
            <p className="text-2xl font-bold text-green-600">
              {totalActionable === 0 ? 'All Clear' : 'Needs Attention'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Queue */}
      <div className="pt-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : invoices.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <ShieldCheck className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">All Invoices Reviewed</h3>
              <p className="text-muted-foreground">
                No invoices are waiting for review. New AI-extracted invoices will appear here
                when they require human verification.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {invoices.map(invoice => (
              <ReviewCard
                key={invoice.id}
                invoice={invoice}
                onApprove={handleApprove}
                onReject={handleReject}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Standalone page (redirected, kept for direct-URL backward compatibility).
export function ReviewQueuePage() {
  return (
    <DashboardLayout>
      <ReviewQueueContent />
    </DashboardLayout>
  );
}
